import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { Badge } from '@mui/material';

export interface Option {
  label: string;
  value: string;
  count?: number;
}

interface DropdownProps {
  /** The label for the dropdown */
  label: string;
  /** Available options */
  options: Option[];
  /** Selected values */
  value: string[];
  /** Callback when selection changes */
  onChange: (selected: string[]) => void;
  /** Whether multiple selection is allowed */
  multiple?: boolean;
  /** Whether to show Select All option */
  allowSelectAll?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** The size of the dropdown */
  size?: 'small' | 'medium';
  /** Custom class names */
  className?: string;
  /** Whether to show the search box */
  showSearch?: boolean;
  /** Width of the dropdown button */
  width?: string | number;
  /** Function to format the option label */
  optionLabel?: (option: Option) => string | React.ReactNode;
  /** Function to format the selected label */
  selectedLabel?: (selected: Option) => string | React.ReactNode;
  /** Whether to show the label on top like a fieldset */
  topLabel?: boolean;
  handleOpenChange?: (open: boolean) => void;
}

const DropdownParent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width?: string | number }>(({ theme, width }) => ({
  width: width || '100%',
  position: 'relative',
  display: 'inline-block',
  '&:hover > span > button': {
    background: theme.palette.grey[200],
    borderColor: theme.palette.divider,
  },
}));

const DropdownButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width?: string | number }>(({ theme, width }) => ({
  minWidth: 'unset',
  maxWidth: 'none',
  width: width || 'auto',
  justifyContent: 'space-between',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  color: 'black',
  fontWeight: 'normal',
  border: `1px solid ${theme.palette.divider}`,
}));

const DropdownLabel = styled('span')({
  position: 'absolute',
  top: '-0.5rem',
  left: '0.75rem',
  backgroundColor: 'white',
  padding: '0 4px',
  fontSize: '0.625rem',
  zIndex: 1,
});

const ButtonWrapper = styled('div')({
  display: 'block',
  width: '100%',
});

const SearchBox = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(1),
  width: 'calc(100% - 16px)',
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
  },
}));

// Replace OptionChip with custom OptionItem component
const OptionItem = styled('div')<{ selected?: boolean; disabled?: boolean }>(
  ({ theme, selected, disabled }) => {
    let backgroundColor = 'transparent';
    let hoverBackgroundColor = theme.palette.action.hover;
    let borderColor = theme.palette.divider;
    let hoverBorderColor = theme.palette.primary.main;

    if (disabled) {
      backgroundColor = theme.palette.action.disabledBackground;
      hoverBackgroundColor = theme.palette.action.disabledBackground;
      hoverBorderColor = theme.palette.divider;
    } else if (selected) {
      backgroundColor = theme.palette.primary.light;
      hoverBackgroundColor = theme.palette.primary.light;
      borderColor = theme.palette.primary.main;
    }

    return {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      width: '100%',
      padding: theme.spacing(1.5),
      margin: theme.spacing(0.25, 0),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${borderColor}`,
      backgroundColor,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
      minHeight: '44px',
      opacity: disabled ? 0.5 : 1,
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
        borderColor: hoverBorderColor,
      },
    };
  },
);

const OptionText = styled('div')<{ disabled?: boolean }>(({ disabled }) => ({
  flex: 1,
  fontSize: '0.875rem',
  lineHeight: 1.3,
  wordWrap: 'break-word',
  paddingRight: '12px',
  color: disabled ? 'text.disabled' : 'inherit',
  // Allow up to 2 lines with proper wrapping
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Use CSS to limit to 2 lines
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const StyledPopover = styled(Popover)(() => ({
  '& .MuiPopover-paper': {
    minWidth: 250,
    width: 'auto !important',
    maxWidth: '350px',
  },
}));

export const CustomDropdown: React.FC<DropdownProps> = ({
  label,
  options,
  value,
  onChange,
  multiple = false,
  allowSelectAll = true,
  disabled = false,
  size = 'medium',
  className,
  showSearch = false,
  width,
  optionLabel,
  selectedLabel,
  topLabel,
  handleOpenChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Filter options based on search
  const filteredOptions = options.filter((option) =>
    showSearch ? option.label.toLowerCase().includes(searchQuery.toLowerCase()) : true,
  );

  const getSelectedLabel = () => {
    if (value.length === 0 || multiple) {
      return label;
    }
    const selectedOption = options.find(
      (opt) => opt.value.toString() === value[0].toString(),
    );
    if (selectedLabel && selectedOption) {
      return selectedLabel(selectedOption);
    }
    return `${label}: ${selectedOption?.label || ''}`;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    handleOpenChange?.(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
    handleOpenChange?.(false);
  };

  const isOptionSelected = (optionValue: string) =>
    value.map((v) => v.toString()).includes(optionValue);

  const handleOptionClick = (optionValue: string) => {
    const isExisting = isOptionSelected(optionValue);
    let newSelected: string[];
    if (multiple) {
      newSelected = isExisting
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
    } else {
      newSelected = isExisting ? [] : [optionValue];
      handleClose();
    }
    onChange(newSelected);
  };

  const handleSelectAll = () => {
    // Only include options that have count > 0 for Select All
    const availableOptions = filteredOptions.filter(
      (opt) => opt.count === undefined || opt.count > 0,
    );
    const allValues = availableOptions.map((opt) => opt.value);
    onChange(value.length === allValues.length ? [] : allValues);
  };

  const getOptionLabel = (option) => {
    if (!optionLabel) {
      return option.label;
    }

    return optionLabel(option);
  };

  const open = Boolean(anchorEl);

  return (
    <ButtonWrapper
      className={className}
      data-testid={`dropdown-container-${label.toLowerCase().replaceAll(' ', '-')}`}
    >
      <DropdownParent width={width}>
        <Badge
          badgeContent={value.length}
          color="primary"
          invisible={!multiple || value.length === 0}
          sx={{ display: 'block' }}
          data-testid={`dropdown-badge-${label.toLowerCase().replaceAll(' ', '-')}`}
        >
          <DropdownButton
            onClick={handleClick}
            variant="outlined"
            disabled={disabled}
            size={size}
            width={width}
            data-testid={`dropdown-${label.toLowerCase().replaceAll(' ', '-')}`}
          >
            {getSelectedLabel()}
          </DropdownButton>
        </Badge>
        {topLabel && value.length > 0 && <DropdownLabel>{label}</DropdownLabel>}
      </DropdownParent>
      <StyledPopover
        key={`${label}-popover`}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: { p: 2 },
          },
        }}
        data-testid={`dropdown-popover-${label.toLowerCase().replaceAll(' ', '-')}`}
      >
        {showSearch && (
          <SearchBox
            placeholder="Search..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
        {multiple && allowSelectAll && (
          <OptionItem
            onClick={handleSelectAll}
            selected={value.length === filteredOptions.length}
            data-testid="dropdown-option-select-all"
          >
            <OptionText>Select All</OptionText>
          </OptionItem>
        )}
        {filteredOptions.map((option) => (
          <OptionItem
            key={option.value}
            onClick={() => handleOptionClick(option.value)}
            selected={isOptionSelected(option.value)}
            data-testid={`dropdown-option-${option.value}`}
          >
            <OptionText data-testid={`dropdown-option-${option.value}`}>
              {getOptionLabel(option)}
            </OptionText>
          </OptionItem>
        ))}
      </StyledPopover>
    </ButtonWrapper>
  );
};
